
/**
 * FieldInput — the single slash-native field-reference input used by every
 * module editor. Replaces the old `{{ }}` TemplateAutocomplete.
 *
 *  - Type `/` to open the field menu; pick a source or workbook field to insert
 *    it as a pill. `{{ }}` is gone entirely. The menu lists matches under a
 *    "Source file" header, then a "Workbook" header.
 *  - Selected fields render as colored pills inside the input via an overlay
 *    layer painted behind a real <textarea>/<input> (caret, IME, undo all
 *    native). Source vs workbook pills are coloured differently.
 *  - Emits a structured `TemplateValue`; never a string.
 *  - A standardized helper line renders under every instance.
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { SourceRow } from "@/lib/mapper/types";
import {
  availableFields,
  type AvailableField,
  type FieldOrigin,
} from "@/modules/_shared/fieldOrigin";
import type { TemplateValue } from "@/modules/_shared/templateValue";
import {
  editingToSegments,
  paintTokens,
  segmentsToEditing,
  type KnownField,
} from "@/modules/_shared/fieldInputText";

/** Standardized helper text shown under every field input. */
export const FIELD_INPUT_HELP =
  "Type / to insert a source or workbook field. Add | upper, | slugify, | default after it.";

export type FieldInputHandle = {
  focus: () => void;
  insertAtCaret: (text: string) => void;
  getElement: () => HTMLInputElement | HTMLTextAreaElement | null;
};

type Props = {
  value: TemplateValue | string | null | undefined;
  onChange: (next: TemplateValue) => void;
  sourceHeaders: string[];
  /** Output (workbook) columns offered alongside source headers. */
  outputColumns?: string[];
  sampleRow?: SourceRow | undefined;
  placeholder?: string;
  multiline?: boolean;
  rows?: number;
  className?: string;
  id?: string;
  /** Hide the standardized helper line (e.g. tight inline rows). Default false. */
  hideHelp?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
};

type ActiveSlash = { openIdx: number; partial: string };

function isSlashBoundary(ch: string | undefined): boolean {
  return ch === undefined || /\s|[([{=+\-*?,]/.test(ch);
}

/** Detect a `/partial` field trigger immediately before the caret. */
function findActiveSlash(text: string, caret: number): ActiveSlash | null {
  const before = text.slice(0, caret);
  const openIdx = before.lastIndexOf("/");
  if (openIdx < 0) return null;
  if (!isSlashBoundary(before[openIdx - 1])) return null;
  const partial = before.slice(openIdx + 1);
  if (/[\n\r/|]/.test(partial)) return null;
  return { openIdx, partial: partial.trim() };
}

function rankSuggestions(pool: AvailableField[], q: string): AvailableField[] {
  if (!q) return pool;
  const ql = q.toLowerCase();
  const starts: AvailableField[] = [];
  const contains: AvailableField[] = [];
  for (const s of pool) {
    const hl = s.name.toLowerCase();
    if (hl.startsWith(ql)) starts.push(s);
    else if (hl.includes(ql)) contains.push(s);
  }
  return [...starts, ...contains];
}

/** One labelled section of the `/` menu. */
export type SuggestionGroup = {
  origin: FieldOrigin;
  label: string;
  fields: AvailableField[];
};

/** Menu section headers, in display order: source file first, then workbook. */
const GROUP_LABELS: readonly { origin: FieldOrigin; label: string }[] = [
  { origin: "source", label: "Source file" },
  { origin: "workbook", label: "Workbook" },
];

/**
 * The `/` menu's matches, grouped by origin — source-file fields first, then
 * workbook fields — each group keeping the starts-with-before-contains
 * ranking. Empty groups are kept (the menu hides their header).
 */
export function groupSuggestions(fields: AvailableField[], query: string): SuggestionGroup[] {
  return GROUP_LABELS.map(({ origin, label }) => ({
    origin,
    label,
    fields: rankSuggestions(
      fields.filter((f) => f.origin === origin),
      query,
    ),
  }));
}

/**
 * The flat, visual order of the grouped menu. Keyboard highlight indexes into
 * this list, so ArrowUp/Down walk the options exactly as they are displayed.
 */
export function flattenSuggestionGroups(groups: SuggestionGroup[]): AvailableField[] {
  return groups.flatMap((g) => g.fields);
}

function sampleText(v: unknown): string {
  if (v == null || v === "") return "";
  return v instanceof Date ? v.toISOString().slice(0, 10) : String(v);
}

function shortPreview(v: unknown): string {
  const s = sampleText(v);
  return s.length > 60 ? s.slice(0, 57) + "..." : s;
}

type SuggestionMenuProps = {
  groups: SuggestionGroup[];
  /** Index into `flattenSuggestionGroups(groups)`. */
  highlight: number;
  sampleRow?: SourceRow;
  onAccept: (field: AvailableField) => void;
  onHighlight: (index: number) => void;
  /** The listbox element (used to scroll the highlighted option into view). */
  listRef?: React.Ref<HTMLDivElement>;
  /**
   * Offset from the top of the input box to place the menu at (just under
   * the caret's line in a multi-line box). Omitted → right under the box.
   */
  top?: number;
};

/**
 * The `/` suggestion listbox: a sticky "Source file" section followed by a
 * "Workbook" section (a header is hidden when its section has no matches).
 * Each option gets the full row width for the field name — wrapping to two
 * lines, then truncating, with the full name in `title` — and a source
 * field's sample value sits underneath in a small muted line.
 */
export function FieldSuggestionMenu({
  groups,
  highlight,
  sampleRow,
  onAccept,
  onHighlight,
  listRef,
  top,
}: SuggestionMenuProps) {
  // Flat index of each group's first option (highlight indexes the flat list).
  const starts = groups.map((_, gi) =>
    groups.slice(0, gi).reduce((n, g) => n + g.fields.length, 0),
  );
  return (
    <div
      ref={listRef}
      role="listbox"
      style={top === undefined ? undefined : { top }}
      className={[
        "absolute left-0 right-0 z-30 max-h-60 overflow-y-auto rounded border border-slate-200 dark:border-border bg-white dark:bg-surface shadow-lg",
        top === undefined ? "top-full mt-1" : "",
      ].join(" ")}
    >
      {groups.map((group, gi) => {
        const start = starts[gi];
        if (group.fields.length === 0) return null;
        const isWorkbookGroup = group.origin === "workbook";
        return (
          <div key={group.origin} role="group" aria-label={group.label}>
            <div
              role="presentation"
              aria-hidden
              className={[
                "sticky top-0 z-10 border-b px-2 py-1 text-[10px] font-semibold uppercase tracking-wider",
                isWorkbookGroup
                  ? "border-violet-100 bg-violet-50 text-violet-700 dark:border-violet-900/50 dark:bg-violet-950 dark:text-violet-300"
                  : "border-slate-100 bg-slate-50 text-slate-500 dark:border-border dark:bg-surface-2 dark:text-slate-400",
              ].join(" ")}
            >
              {group.label}
            </div>
            {group.fields.map((field, j) => {
              const i = start + j;
              const isWorkbook = field.origin === "workbook";
              const rawSample = !isWorkbook && sampleRow ? sampleText(sampleRow[field.name]) : "";
              const sample = shortPreview(rawSample);
              const isActive = i === highlight;
              return (
                <button
                  key={`${field.origin}:${field.name}`}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  data-suggestion-index={i}
                  title={rawSample ? `${field.name}\n${rawSample}` : field.name}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    onAccept(field);
                  }}
                  onMouseEnter={() => onHighlight(i)}
                  className={[
                    "block w-full min-w-0 px-2 py-1 text-left text-[12px]",
                    isActive
                      ? isWorkbook
                        ? "bg-violet-700 text-white"
                        : "bg-slate-900 dark:bg-slate-100 dark:text-slate-950 text-white"
                      : isWorkbook
                        ? "text-violet-900 dark:text-violet-200 hover:bg-violet-50 dark:hover:bg-violet-950/40"
                        : "text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-surface-2",
                  ].join(" ")}
                >
                  <span className="min-w-0 font-mono leading-4 break-words line-clamp-2">
                    {field.name}
                  </span>
                  {sample && (
                    <span className="mt-0.5 block min-w-0 truncate font-mono text-[10.5px] leading-4 opacity-60">
                      {sample}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// Background + ring only — NO horizontal padding (padding would shift the
// painted glyphs relative to the textarea caret). The ring is a box-shadow, so
// it gives the pill visual breathing room without affecting text layout.
const pillCls = (origin: KnownField["origin"]) =>
  origin === "workbook"
    ? "rounded bg-violet-100 ring-2 ring-violet-100 dark:bg-violet-900/50 dark:ring-violet-900/50"
    : "rounded bg-sky-100 ring-2 ring-sky-100 dark:bg-sky-900/50 dark:ring-sky-900/50";

export const FieldInput = forwardRef<FieldInputHandle, Props>(function FieldInput(
  {
    value,
    onChange,
    sourceHeaders,
    outputColumns,
    sampleRow,
    placeholder,
    multiline = false,
    rows = 4,
    className,
    id,
    hideHelp = false,
    onFocus,
    onBlur,
  },
  ref,
) {
  const innerRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);
  const [caret, setCaret] = useState(0);
  const [highlight, setHighlight] = useState(0);
  const [open, setOpen] = useState(false);
  const pendingCaret = useRef<number | null>(null);

  const fields = useMemo(
    () => availableFields(sourceHeaders, outputColumns ?? []),
    [sourceHeaders, outputColumns],
  );
  const known: KnownField[] = fields;

  // Editing string derived from the structured value (source of truth).
  const text = useMemo(() => segmentsToEditing(value ?? []), [value]);

  const emit = useCallback(
    (nextText: string) => onChange(editingToSegments(nextText, known)),
    [onChange, known],
  );

  useImperativeHandle(
    ref,
    () => ({
      focus: () => innerRef.current?.focus(),
      getElement: () => innerRef.current,
      insertAtCaret: (insert: string) => {
        const el = innerRef.current;
        if (!el) return;
        const start = el.selectionStart ?? el.value.length;
        const end = el.selectionEnd ?? el.value.length;
        const next = el.value.slice(0, start) + insert + el.value.slice(end);
        pendingCaret.current = start + insert.length;
        emit(next);
      },
    }),
    [emit],
  );

  useLayoutEffect(() => {
    const want = pendingCaret.current;
    if (want == null) return;
    pendingCaret.current = null;
    const el = innerRef.current;
    if (!el) return;
    el.focus();
    try {
      el.setSelectionRange(want, want);
    } catch {
      /* unsupported on some input types */
    }
    setCaret(want);
  }, [text]);

  const active = useMemo(() => findActiveSlash(text, caret), [text, caret]);
  // Grouped (source file, then workbook); `suggestions` is the same list in
  // visual order, which is what the keyboard highlight indexes into.
  const groups = useMemo(
    () => (active ? groupSuggestions(fields, active.partial) : []),
    [active, fields],
  );
  const suggestions = useMemo(() => flattenSuggestionGroups(groups), [groups]);

  // Open/close + reset highlight when the active trigger changes (adjusting
  // state during render, guarded so it runs once per change).
  const activeKey = active ? `${active.openIdx}:${active.partial}` : null;
  const menuKey = `${activeKey}#${suggestions.length}`;
  const [seenMenuKey, setSeenMenuKey] = useState<string | null>(null);
  if (seenMenuKey !== menuKey) {
    setSeenMenuKey(menuKey);
    setOpen(Boolean(active) && suggestions.length > 0);
    setHighlight(0);
  }

  const acceptSuggestion = useCallback(
    (field: AvailableField) => {
      if (!active) return;
      const before = text.slice(0, active.openIdx);
      const afterCaret = text.slice(caret);
      const token = field.origin === "workbook" ? `/out:${field.name}` : `/${field.name}`;
      const next = before + token + afterCaret;
      pendingCaret.current = (before + token).length;
      emit(next);
      setOpen(false);
    },
    [active, text, caret, emit],
  );

  const menuRef = useRef<HTMLDivElement | null>(null);
  // In a multi-line box the menu opens under the caret's line (a tall prompt
  // box would otherwise push it out of view). A hidden mirror with the same
  // text metrics locates the caret.
  const caretMarkRef = useRef<HTMLSpanElement | null>(null);
  const [menuTop, setMenuTop] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    if (!multiline || !open) return;
    const mark = caretMarkRef.current;
    const box = innerRef.current;
    if (!mark || !box) return;
    const lineBottom = mark.offsetTop - box.scrollTop + mark.offsetHeight;
    setMenuTop(Math.max(0, Math.min(lineBottom, box.offsetHeight)) + 4);
  }, [multiline, open, text, caret]);
  /** Set by ArrowUp/Down so only keyboard moves scroll the listbox. */
  const scrollToHighlight = useRef(false);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (!open || suggestions.length === 0) return;
      if (e.key === "ArrowDown") {
        e.preventDefault();
        scrollToHighlight.current = true;
        setHighlight((h) => (h + 1) % suggestions.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        scrollToHighlight.current = true;
        setHighlight((h) => (h - 1 + suggestions.length) % suggestions.length);
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        acceptSuggestion(suggestions[highlight] ?? suggestions[0]);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
      }
    },
    [acceptSuggestion, highlight, open, suggestions],
  );

  // Keep the keyboard-highlighted option visible (below its sticky group
  // header) as it crosses sections. Scrolls the listbox only, never the page.
  useEffect(() => {
    if (!open || !scrollToHighlight.current) return;
    scrollToHighlight.current = false;
    const list = menuRef.current;
    const option = list?.querySelector<HTMLElement>(`[data-suggestion-index="${highlight}"]`);
    if (!list || !option) return;
    const headerHeight =
      (option.parentElement?.firstElementChild as HTMLElement | null)?.offsetHeight ?? 0;
    const top = option.offsetTop - headerHeight;
    const bottom = option.offsetTop + option.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) list.scrollTop = bottom - list.clientHeight;
  }, [highlight, open]);

  const syncCaret = useCallback(() => {
    const el = innerRef.current;
    if (!el) return;
    setCaret(el.selectionStart ?? el.value.length);
  }, []);

  const syncScroll = useCallback(() => {
    const el = innerRef.current;
    const bd = backdropRef.current;
    if (!el || !bd) return;
    bd.scrollTop = el.scrollTop;
    bd.scrollLeft = el.scrollLeft;
  }, []);

  // Shared box geometry so the textarea text sits exactly over the backdrop.
  const boxCls =
    "w-full rounded border px-2 py-1 font-mono text-[12px] leading-5";
  const borderCls =
    "border-slate-300 dark:border-border focus:border-slate-500 dark:focus:border-slate-400 focus:outline-none focus:ring-1 focus:ring-slate-300 dark:focus:ring-slate-600";

  const tokens = useMemo(() => paintTokens(text, known), [text, known]);

  const commonProps = {
    id,
    value: text,
    placeholder,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      emit(e.target.value);
      requestAnimationFrame(syncCaret);
    },
    onKeyDown: handleKeyDown,
    onKeyUp: syncCaret,
    onClick: syncCaret,
    onSelect: syncCaret,
    onScroll: syncScroll,
    onFocus: () => {
      syncCaret();
      onFocus?.();
    },
    onBlur: () => {
      setTimeout(() => setOpen(false), 120);
      onBlur?.();
    },
    spellCheck: false,
    // The textarea holds the VISIBLE glyphs (so the caret aligns exactly with
    // the text); its background is transparent so the backdrop pill highlights
    // show through behind the matching tokens.
    className: `${boxCls} ${borderCls} ${className ?? ""} relative z-10 bg-transparent text-slate-900 dark:text-slate-100 resize-none`,
  } as const;

  return (
    <div>
      <div className="relative">
        {/* Backdrop: transparent text (identical metrics to the textarea) that
            paints ONLY the pill backgrounds behind field tokens. Pills use a
            box-shadow ring for breathing room — never horizontal padding, which
            would shift glyphs out of sync with the textarea caret. */}
        <div
          ref={backdropRef}
          aria-hidden
          className={`${boxCls} border-transparent pointer-events-none absolute inset-0 z-0 overflow-hidden whitespace-pre-wrap break-words text-transparent`}
        >
          {tokens.map((tok, i) =>
            tok.kind === "text" ? (
              <span key={i}>{tok.text}</span>
            ) : (
              <span key={i} className={pillCls(tok.origin)}>
                {tok.text}
              </span>
            ),
          )}
          {/* Trailing newline keeps the last line measurable in a textarea. */}
          {"\n"}
        </div>

        {multiline && open && (
          <div
            aria-hidden
            className={`${boxCls} border-transparent pointer-events-none invisible absolute inset-0 overflow-hidden whitespace-pre-wrap break-words`}
          >
            {text.slice(0, caret)}
            <span ref={caretMarkRef}>{"\u200b"}</span>
          </div>
        )}

        {multiline ? (
          <textarea
            {...commonProps}
            ref={(el) => {
              innerRef.current = el;
            }}
            rows={rows}
          />
        ) : (
          <input
            {...commonProps}
            ref={(el) => {
              innerRef.current = el;
            }}
            type="text"
          />
        )}

        {open && suggestions.length > 0 && (
          <FieldSuggestionMenu
            groups={groups}
            highlight={highlight}
            sampleRow={sampleRow}
            onAccept={acceptSuggestion}
            onHighlight={setHighlight}
            listRef={menuRef}
            top={multiline ? menuTop : undefined}
          />
        )}
      </div>
      {!hideHelp && (
        <p className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">{FIELD_INPUT_HELP}</p>
      )}
    </div>
  );
});
